import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import 'normalize.css'
import type { PostHogConfig } from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import 'unfonts.css'
import 'vite/modulepreload-polyfill'
import { App } from '~/App'
import '~/App.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false
    }
  }
})

const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string | undefined
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST as
  | string
  | undefined
const isPublicShortlistPage = window.location.pathname.startsWith('/shortlist/')
const isPostHogEnabled =
  !isPublicShortlistPage &&
  import.meta.env.MODE === 'production' &&
  import.meta.env.VITE_ENABLE_POSTHOG === 'true' &&
  Boolean(posthogKey) &&
  Boolean(posthogHost)

const posthogOptions: Partial<PostHogConfig> = isPostHogEnabled
  ? {
      api_host: posthogHost,
      person_profiles: 'identified_only',
      defaults: '2025-05-24'
    }
  : {}

if (import.meta.env.MODE === 'production' && !isPublicShortlistPage) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_FRONTEND_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration()
    ],
    beforeSend(event) {
      const hasOpenTechUxFrame =
        event.exception?.values?.some((exception) =>
          exception.stacktrace?.frames?.some(
            (frame) =>
              frame.filename?.includes(
                '/lom-captor/dist/opentech-ux-lib.js'
              ) === true
          )
        ) === true

      return hasOpenTechUxFrame ? null : event
    },
    tracesSampleRate: 1.0,
    tracePropagationTargets: ['localhost', /^https:\/\/api\.job-explorer\.io/],
    replaysSessionSampleRate: import.meta.env.DEV ? 100 : 0.1,
    replaysOnErrorSampleRate: 1.0
  })
}

const params = new URLSearchParams(window.location.search)
if (params.get('from') === 'logout') {
  localStorage.removeItem('token')
  localStorage.removeItem('id')
  localStorage.removeItem('email')
  localStorage.removeItem('mainToken')
  localStorage.removeItem('mainUser')
  localStorage.removeItem('mainId')
  params.delete('from')
  window.location.href = `${window.location.pathname}?${params.toString()}`
} else if (params.has('jwt')) {
  // Bridge from legacy app
  if (params.has('userId')) {
    if (params.get('userId') !== localStorage.getItem('id')) {
      localStorage.setItem('mainToken', localStorage.getItem('token') ?? '')
      localStorage.setItem('mainUser', localStorage.getItem('email') ?? '')
      localStorage.setItem('mainId', localStorage.getItem('id') ?? '')
    } else {
      localStorage.removeItem('mainToken')
      localStorage.removeItem('mainUser')
      localStorage.removeItem('mainId')
    }
    localStorage.setItem('id', params.get('userId') ?? '')
  }
  localStorage.setItem('token', params.get('jwt') ?? '')
  if (params.has('instance')) {
    localStorage.setItem('instance', params.get('instance') ?? '')
  }
  params.delete('jwt')
  params.delete('instance')
  params.delete('userId')
  window.location.href = `${window.location.pathname}?${params.toString()}`
} else {
  document.addEventListener('DOMContentLoaded', () => {
    const appElement = document.querySelector('#app')
    if (appElement !== null) {
      const app = (
        <QueryClientProvider client={queryClient}>
          <App />
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      )

      createRoot(appElement).render(
        <StrictMode>
          {isPostHogEnabled && posthogKey ? (
            <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
              {app}
            </PostHogProvider>
          ) : (
            app
          )}
        </StrictMode>
      )
    }
  })
}
